import type { SalesOrder } from '../../types/salesOrder';

// Brinde de promoção do site na nota fiscal.
//
// Um pedido do site com brinde ("compre X ganhe Y") sai em UMA nota de venda: o
// brinde é declarado a VALOR CHEIO e o valor dele entra como desconto do pedido,
// de modo que o vNF continue igual ao que a cliente pagou. Emitir uma segunda
// nota de bonificação exigiria um segundo pedido de venda, e a mercadoria toda vai
// no mesmo pacote — um pacote, uma nota.
//
// Sob o Simples Nacional (CRT=1, CSOSN 102) o XML não declara base de ICMS por
// item, então como o desconto se distribui entre as linhas não tem efeito
// tributário; o rateio por item que o backend faz (prorateByWeight) existe apenas
// para fechar a soma com o ICMSTot, que a SEFAZ exige.
//
// ESCOPO DELIBERADO: só pedido do site. Brinde de proposta do CRM e componente de
// kit continuam indo na NF de bonificação, como sempre — eles chegam com valor
// líquido zero e nada neste arquivo os alcança.
//
// Extraído para ser testável sem renderizar o formulário (mesmo padrão de
// paymentPlan.ts e invoiceItemPricing.ts).

/**
 * Pedido nascido no site.
 *
 * O discriminador é o `created_by` que o intake carimba — o mesmo que o ERPApi usa
 * para decidir o sync de status com o site. `order_num_ecommerce` entra como rede
 * para pedidos antigos, anteriores ao carimbo.
 */
export function isEcommerceOrder(orderData: SalesOrder | undefined): boolean {
  if (!orderData) return false;
  if (orderData.created_by === 'system:ecommerce') return true;
  return !!orderData.order_num_ecommerce;
}

/**
 * O brinde deste pedido viaja na NF de VENDA (e portanto NÃO cabe NF de
 * bonificação).
 *
 * Existe porque a tela do pedido oferecia os dois botões — "Emitir NF Venda" e
 * "Emitir NF Bonificação" — sempre que havia item `is_gift`, e num pedido do site
 * a segunda nota é justamente o caminho que a decisão fiscal descartou. Pior:
 * como `buildItemsFromOrder` exclui o brinde do site da nota de bonificação, o
 * botão levaria a uma nota sem item nenhum. Convite/pedido 100% presente
 * (`fullGiftOrder`) continua tendo sua nota própria.
 */
export function giftRidesSalesInvoice(
  orderData: SalesOrder | undefined,
  fullGiftOrder: boolean,
): boolean {
  return !fullGiftOrder && isEcommerceOrder(orderData);
}

/**
 * Valor total dos itens de brinde de um pedido do site, a valor cheio — o quanto
 * entra como desconto na NF de venda.
 *
 * Fora de pedido do site devolve 0, então nada muda nos fluxos existentes.
 */
export function giftDiscountFromOrder(orderData: SalesOrder | undefined): number {
  if (!isEcommerceOrder(orderData) || !orderData?.items) return 0;
  const total = orderData.items
    .filter(i => i.is_gift === true)
    .reduce((sum, i) => sum + (i.unit_price || 0) * i.quantity, 0);
  return Math.round(total * 100) / 100;
}
